import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { EmptyList } from "../../Components/EmptyList";
import { Button } from "../../Components/Button";
import { CheckIcon, ExclamationIcon } from "@heroicons/react/outline";
import {
  requestListUserSubscription,
  useAuthState,
  useSubscriptionDispatch,
  useSubscriptionState,
} from "../../Context";
import { ErrorList } from "../../Components/ErrorList";
import { isLastTokenNull } from "../../Utils";

const ITEMS_PER_PAGE = 10;

const MySubscriptions = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useSubscriptionDispatch();

  const { userSubscriptions, loading, error, nextTokens, nextLoading } =
    useSubscriptionState();
  const { user } = useAuthState();

  const [page, setPage] = useState(1);

  useEffect(() => {
    if (user.id) {
      requestListUserSubscription(dispatch, {
        userID: user.id,
        page: 1,
        limit: ITEMS_PER_PAGE,
      });
    }
  }, [dispatch, user.id]);

  const loadMore = () => {
    const nextPage = error ? page : page + 1;
    setPage(nextPage);
    requestListUserSubscription(dispatch, {
      userID: user.id,
      page: nextPage,
      limit: ITEMS_PER_PAGE,
      nextToken: nextTokens[nextPage],
    });
  };

  const handleRenewSubscription = () => {
    navigate("/subscription");
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "ACTIVE":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckIcon className="w-3 h-3 mr-1" />
            {t("Active")}
          </span>
        );
      case "EXPIRING_SOON":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <ExclamationIcon className="w-3 h-3 mr-1" />
            {t("expiringSoon")}
          </span>
        );
      case "EXPIRED":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <ExclamationIcon className="w-3 h-3 mr-1" />
            {t("Expired")}
          </span>
        );
      default:
        return null;
    }
  };

  const processedSubscriptions = useMemo(() => {
    return userSubscriptions.map((subscription) => {
      const isExpired = dayjs().isAfter(dayjs(subscription.expiredAt));
      const daysUntilExpiry = dayjs(subscription.expiredAt).diff(
        dayjs(),
        "days"
      );
      const isExpiringSoon = !isExpired && daysUntilExpiry <= 1;

      let status;
      if (isExpired) {
        status = "EXPIRED";
      } else if (isExpiringSoon) {
        status = "EXPIRING_SOON";
      } else {
        status = "ACTIVE";
      }

      return {
        ...subscription,
        isExpired,
        daysUntilExpiry,
        status,
      };
    });
  }, [userSubscriptions]);

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <Button onClick={handleRenewSubscription} title="browsePlans" />
        </div>
      </div>

      {/* Subscriptions List */}
      <div className="space-y-6">
        {loading && page === 1 ? (
          // Loading skeleton
          Array.from({ length: 2 }).map((_, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="h-6 bg-gray-300 rounded-md w-1/3 mb-2"></div>
                  <div className="h-4 bg-gray-300 rounded-md w-2/3 mb-4"></div>
                  <div className="h-4 bg-gray-300 rounded-md w-1/4 mb-2"></div>
                  <div className="h-4 bg-gray-300 rounded-md w-1/3"></div>
                </div>
                <div className="h-6 bg-gray-300 rounded-lg w-20"></div>
              </div>
            </div>
          ))
        ) : processedSubscriptions.length > 0 ? (
          <>
            {processedSubscriptions.map((subscription) => (
              <div
                key={subscription.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {subscription.package?.title ||
                            subscription.voucher?.title}
                        </h3>
                        {getStatusBadge(subscription.status)}
                      </div>

                      <p className="text-gray-600 mb-4">
                        {subscription.package?.description ||
                          subscription.voucher?.description}
                      </p>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-500">
                            {t("price")}
                          </span>
                          <div className="mt-1">
                            <NumericFormat
                              displayType={"text"}
                              value={subscription.package?.price || 0}
                              thousandSeparator={"."}
                              decimalSeparator={","}
                              prefix={"Rp"}
                              className="font-semibold text-gray-900"
                            />
                            {subscription.package?.periodType === "MONTHLY" && (
                              <span className="text-gray-500">
                                /{t("month")}
                              </span>
                            )}
                            {subscription.package?.periodType === "YEARLY" && (
                              <span className="text-gray-500">
                                /{t("year")}
                              </span>
                            )}
                          </div>
                        </div>

                        <div>
                          <span className="font-medium text-gray-500">
                            {t("expires")}
                          </span>
                          <div className="mt-1 font-semibold text-gray-900">
                            {dayjs(subscription.expiredAt)
                              .locale(i18n.language)
                              .format("DD MMM YYYY HH:mm")}
                          </div>
                        </div>
                      </div>

                      {/* Features */}
                      {subscription.package?.features &&
                        subscription.package.features.length > 0 && (
                          <div className="mt-4">
                            <span className="font-medium text-gray-500 text-sm">
                              {t("features")}
                            </span>
                            <ul className="mt-2 space-y-1">
                              {subscription.package.features
                                .slice(0, 3)
                                .map((feature, index) => (
                                  <li
                                    key={index}
                                    className="flex items-center text-sm text-gray-600"
                                  >
                                    <CheckIcon className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                                    {t(feature)}
                                  </li>
                                ))}
                              {subscription.package.features.length > 3 && (
                                <li className="text-sm text-gray-500">
                                  +{subscription.package.features.length - 3}{" "}
                                  {t("moreFeatures")}
                                </li>
                              )}
                            </ul>
                          </div>
                        )}
                    </div>

                    <div className="ml-6 flex flex-col gap-2">
                      {subscription.status === "EXPIRED" ||
                      subscription.status === "EXPIRING_SOON" ? (
                        <Button
                          onClick={handleRenewSubscription}
                          title="renew"
                          outline
                        />
                      ) : (
                        <div className="text-right">
                          <div className="text-sm text-gray-500">
                            {subscription.daysUntilExpiry > 0 && (
                              <span>
                                {subscription.daysUntilExpiry} {t("daysLeft")}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {isLastTokenNull(nextTokens) ? null : (
              <div className="text-center pt-4">
                <Button
                  onClick={loadMore}
                  title="Load More"
                  outline
                  loading={nextLoading}
                  disabled={nextLoading}
                />
              </div>
            )}
          </>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            {error ? (
              <ErrorList
                message={error?.message || "Error fetching subscriptions."}
                errors={error?.errors}
              />
            ) : (
              <EmptyList title={t("noActiveSubscriptions")} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MySubscriptions;
